import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { formatRupiah, formatDateID } from "@/lib/format";
import { ArrowRightLeft, PlayCircle, RotateCcw, Undo2, PlusCircle, Trash2, ShieldAlert, MoreHorizontal, Pencil } from "lucide-react";

function Stat({ label, value, tone = "slate" }) {
  const map = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
    slate: "bg-slate-50 border-slate-200 text-slate-900",
  };
  return (
    <div className={`border rounded-md p-4 ${map[tone]}`} data-testid={`stat-${label.replace(/\s+/g, '-').toLowerCase()}`}>
      <div className="text-xs opacity-80">{label}</div>
      <div className="font-mono-num text-lg font-semibold mt-1">{value}</div>
    </div>
  );
}

export function OpeningBalancePage() {
  const navigate = useNavigate();
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [migDate, setMigDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [manualForm, setManualForm] = useState({
    kind: "AR", party_code: "", party_name: "", reference: "",
    date: "", due_date: "", currency: "IDR", exchange_rate: 1,
    original_amount: 0, outstanding_amount: 0,
    account_code: "", debit_or_credit: "DEBIT", notes: "",
  });
  const [showManual, setShowManual] = useState(false);
  const [submittingManual, setSubmittingManual] = useState(false);
  const [coa, setCoa] = useState([]);

  const load = async () => {
    try {
      const [{ data: st }, { data: pv }] = await Promise.all([
        api.get("/opening/status"),
        api.get("/opening/preview"),
      ]);
      setStatus(st);
      setPreview(pv);
      setMigDate(st.migration_date || "2026-01-01");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  useEffect(() => {
    load();
    api.get("/masters/chart_of_accounts").then(({ data }) => setCoa((data || []).filter((c) => c.status === "ACTIVE" && c.allow_direct_posting)));
  }, []);

  const saveDate = async () => {
    try {
      await api.put("/opening/date", { date: migDate });
      toast.success("Tanggal migrasi tersimpan.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const addManual = async () => {
    if (submittingManual) return; // double-submit guard
    // ===== VALIDASI (Bahasa Indonesia) =====
    const f = manualForm;
    if (f.kind !== "OTHER") {
      if (!f.party_code?.trim() && !f.party_name?.trim()) {
        toast.error("Isi minimal Kode atau Nama Rekanan.");
        return;
      }
      if (!f.reference?.trim()) {
        toast.error("Nomor referensi/invoice wajib diisi.");
        return;
      }
      if (!f.due_date) {
        toast.error("Jatuh tempo wajib diisi.");
        return;
      }
      if (f.due_date < f.date) {
        toast.error("Jatuh tempo tidak boleh lebih awal dari tanggal transaksi.");
        return;
      }
    }
    if (f.kind === "OTHER") {
      if (!f.account_code) {
        toast.error("Pilih akun COA terlebih dahulu.");
        return;
      }
      if (!["DEBIT", "KREDIT"].includes(f.debit_or_credit)) {
        toast.error("Sisi harus Debit atau Kredit.");
        return;
      }
    }
    if (!f.date) {
      toast.error("Tanggal wajib diisi.");
      return;
    }
    if (!f.currency?.trim()) {
      toast.error("Mata uang wajib diisi.");
      return;
    }
    const rate = parseFloat(f.exchange_rate);
    if (!rate || rate <= 0) {
      toast.error("Kurs harus lebih besar dari 0.");
      return;
    }
    const outstanding = parseFloat(f.outstanding_amount);
    if (!outstanding || outstanding <= 0) {
      toast.error("Nilai outstanding harus lebih besar dari 0.");
      return;
    }
    setSubmittingManual(true);
    try {
      const payload = { ...f };
      if (payload.kind !== "OTHER") { payload.account_code = ""; payload.debit_or_credit = "DEBIT"; }
      // Default: nilai original = outstanding bila belum diisi
      if (payload.kind !== "OTHER" && (!payload.original_amount || payload.original_amount <= 0)) {
        payload.original_amount = outstanding;
      }
      await api.post("/opening/manual", payload);
      toast.success("Entri manual ditambahkan.");
      setShowManual(false);
      setManualForm({ ...manualForm, party_code: "", party_name: "", reference: "", original_amount: 0, outstanding_amount: 0, notes: "" });
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
      // Modal TIDAK ditutup saat submit gagal — user bisa koreksi
    } finally {
      setSubmittingManual(false);
    }
  };

  const deleteManual = async (id) => {
    if (!window.confirm("Hapus entri saldo awal ini?\n\nPastikan hanya entri yang belum diposting yang boleh dihapus. Perubahan akan memperbarui pratinjau migrasi.")) return;
    try {
      await api.delete(`/opening/manual/${id}`);
      toast.success("Entri dihapus.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ===== EDIT STATE =====
  const [editMode, setEditMode] = useState(null); // "BANK"|"KAS"|"AR"|"AP"|"OTHER"|null
  const [editForm, setEditForm] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);
  const [confirmEdit, setConfirmEdit] = useState(false);

  const openEdit = (mode, row) => {
    setEditMode(mode);
    setConfirmEdit(false);
    if (mode === "BANK") {
      setEditForm({ code: row.code, name: row.name, account_number: row.account_number || "",
                    coa_code: row.coa_code, opening_balance: Number(row.amount || 0),
                    edit_reason: "" });
    } else if (mode === "KAS") {
      setEditForm({ code: row.code, name: row.name, coa_code: row.coa_code,
                    opening_balance: Number(row.amount || 0), edit_reason: "" });
    } else {
      // AR / AP / OTHER (manual entry)
      setEditForm({
        id: row.id, kind: row.kind || mode,
        party_code: row.party_code || "", party_name: row.party_name || "",
        reference: row.reference || "", date: row.date || "", due_date: row.due_date || "",
        currency: row.currency || "IDR", exchange_rate: Number(row.exchange_rate || 1),
        original_amount: Number(row.original_amount || 0),
        outstanding_amount: Number(row.outstanding_amount || 0),
        account_code: row.account_code || "", debit_or_credit: row.debit_or_credit || "DEBIT",
        notes: row.notes || "", edit_reason: "",
      });
    }
  };

  const closeEdit = () => { setEditMode(null); setEditForm(null); setConfirmEdit(false); };

  const saveEdit = async () => {
    if (!editForm) return;
    const f = editForm;
    // Client-side validation ringkas
    if (editMode === "BANK" || editMode === "KAS") {
      if (f.opening_balance == null || Number(f.opening_balance) < 0) {
        toast.error("Saldo awal tidak boleh negatif."); return;
      }
      if (!f.coa_code) { toast.error("Akun COA wajib dipilih."); return; }
    } else {
      if (!f.outstanding_amount || Number(f.outstanding_amount) <= 0) {
        toast.error("Nilai outstanding harus lebih dari nol."); return;
      }
      if (!f.exchange_rate || Number(f.exchange_rate) <= 0) {
        toast.error("Kurs harus lebih dari nol."); return;
      }
      if (f.kind !== "OTHER") {
        if (!f.reference?.trim()) { toast.error("Nomor referensi wajib diisi."); return; }
        if (f.due_date && f.date && f.due_date < f.date) {
          toast.error("Jatuh tempo tidak boleh lebih awal dari tanggal."); return;
        }
      }
      if (f.kind === "OTHER" && !f.account_code) { toast.error("Akun COA wajib dipilih."); return; }
    }
    if (!confirmEdit) { setConfirmEdit(true); return; }  // minta konfirmasi dulu
    setSavingEdit(true);
    try {
      if (editMode === "BANK") {
        await api.put(`/opening/bank/${f.code}`, {
          opening_balance: Number(f.opening_balance), coa_code: f.coa_code,
          edit_reason: f.edit_reason || null,
        });
      } else if (editMode === "KAS") {
        await api.put(`/opening/cash/${f.code}`, {
          opening_balance: Number(f.opening_balance), coa_code: f.coa_code,
          edit_reason: f.edit_reason || null,
        });
      } else {
        const payload = {
          party_code: f.party_code || null, party_name: f.party_name || null,
          reference: f.reference || null, date: f.date || null, due_date: f.due_date || null,
          currency: f.currency, exchange_rate: Number(f.exchange_rate),
          original_amount: Number(f.original_amount || f.outstanding_amount || 0),
          outstanding_amount: Number(f.outstanding_amount),
          notes: f.notes || null, edit_reason: f.edit_reason || null,
        };
        if (f.kind === "OTHER") {
          payload.account_code = f.account_code;
          payload.debit_or_credit = f.debit_or_credit;
        }
        await api.put(`/opening/manual/${f.id}`, payload);
      }
      toast.success("Perubahan tersimpan. Pratinjau migrasi diperbarui.");
      closeEdit();
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSavingEdit(false); }
  };

  const postOpening = async () => {
    if (!preview) return;
    if (Math.abs(preview.totals.difference) > 0.01) {
      toast.error("Debit ≠ Kredit — posting ditolak.");
      return;
    }
    if (!window.confirm("Migrasi saldo awal akan menghasilkan jurnal pembukaan dan memengaruhi Buku Besar serta laporan keuangan. Apakah Anda yakin ingin melanjutkan?")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/opening/post");
      if (data.already_posted) toast.info("Migrasi sudah pernah diposting."); else toast.success(`Jurnal ${data.journal_number} berhasil diposting.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const reverseOpening = async () => {
    const note = window.prompt("Alasan pembalikan migrasi saldo awal:");
    if (!note) return;
    setLoading(true);
    try {
      const { data } = await api.post("/opening/reverse", { note });
      toast.success(`Pembalik ${data.reversal_number} diposting.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const resetLegacy = async () => {
    if (!window.confirm("PERINGATAN: Ini akan menghapus SEMUA transaksi & jurnal (kecuali OPENING_BALANCE) untuk memulai dengan bersih. Lanjutkan?")) return;
    setLoading(true);
    try {
      const { data } = await api.post("/opening/reset-legacy");
      toast.success(`Reset selesai. ${Object.values(data.deleted).reduce((a, b) => a + b, 0)} record dihapus.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  if (!status || !preview) return <div className="p-8 text-slate-500">Memuat data migrasi...</div>;

  const t = preview.totals;
  const c = preview.counts;
  const isPosted = status.status === "DIPOSTING";
  const isReversed = status.status === "DIBALIK";
  const canPost = !isPosted && Math.abs(t.difference) < 0.01 && (c.bank + c.cash + c.ar_auto + c.ap_auto + c.ar_manual + c.ap_manual + c.other_manual) > 0;

  return (
    <div className="space-y-6 page-fade" data-testid="opening-balance-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-slate-500 text-sm mb-1">
            <ArrowRightLeft className="h-4 w-4" />
            <span>Akuntansi › Migrasi Saldo Awal</span>
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight">Migrasi Saldo Awal</h1>
          <p className="text-slate-500 mt-2">Bawa saldo Kas, Bank, Piutang, Hutang, dan akun lain ke Buku Besar sebagai jurnal pembukaan.</p>
        </div>
        <div className="flex gap-2">
          {isPosted && (
            <Button variant="outline" onClick={reverseOpening} disabled={loading} data-testid="reverse-opening-btn">
              <Undo2 className="h-4 w-4 mr-1.5" /> Balik Migrasi
            </Button>
          )}
          {!isPosted && (
            <Button variant="outline" className="border-rose-300 text-rose-700 hover:bg-rose-50" onClick={resetLegacy} disabled={loading} data-testid="reset-legacy-btn">
              <RotateCcw className="h-4 w-4 mr-1.5" /> Reset Data Legacy
            </Button>
          )}
        </div>
      </div>

      {/* Status */}
      <Card>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-4">
              <Badge className={isPosted ? "bg-emerald-600" : isReversed ? "bg-amber-600" : "bg-slate-600"} data-testid="migration-status">
                {isPosted ? "DIPOSTING" : isReversed ? "DIBALIK" : "DRAF"}
              </Badge>
              {status.posted_journal_number && (
                <div className="text-sm">
                  Jurnal Migrasi: <span className="font-mono-num font-semibold">{status.posted_journal_number}</span>
                  {status.posted_by && <span className="text-slate-500 ml-2">oleh {status.posted_by} · {status.posted_at ? formatDateID(status.posted_at) : ""}</span>}
                </div>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Label htmlFor="mig-date" className="text-sm">Tanggal Migrasi</Label>
              <Input id="mig-date" type="date" value={migDate} onChange={(e) => setMigDate(e.target.value)} disabled={isPosted} className="w-44" data-testid="migration-date-input" />
              <Button size="sm" variant="outline" onClick={saveDate} disabled={isPosted || loading} data-testid="save-date-btn">Simpan Tanggal</Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Preview summary */}
      <div>
        <h2 className="font-display text-xl font-semibold mb-3">Pratinjau Migrasi</h2>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <Stat label="Jumlah Bank" value={c.bank} />
          <Stat label="Jumlah Kas" value={c.cash} />
          <Stat label="Jumlah AR (Otomatis)" value={c.ar_auto} />
          <Stat label="Jumlah AR (Manual)" value={c.ar_manual} />
          <Stat label="Jumlah AP (Otomatis)" value={c.ap_auto} />
          <Stat label="Jumlah AP (Manual)" value={c.ap_manual} />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">
          <Stat label="Total Debit" value={formatRupiah(t.posting_debit)} tone="emerald" />
          <Stat label="Total Kredit" value={formatRupiah(t.posting_credit)} tone="emerald" />
          <Stat label="Selisih" value={formatRupiah(t.difference)} tone={Math.abs(t.difference) < 0.01 ? "emerald" : "rose"} />
          <Stat label="Plug Ekuitas" value={formatRupiah(t.equity_plug)} tone="amber" />
        </div>
      </div>

      {/* Bank */}
      <Card>
        <CardContent className="p-4">
          <div className="font-display font-semibold mb-3">Saldo Awal Bank ({c.bank})</div>
          <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>No. Rekening</TableHead><TableHead>Akun COA</TableHead><TableHead className="text-right">Saldo Awal</TableHead>{!isPosted && <TableHead className="text-right">Aksi</TableHead>}</TableRow></TableHeader>
            <TableBody>{preview.bank.length === 0 ? <TableRow><TableCell colSpan={isPosted ? 5 : 6} className="text-center py-4 text-slate-500">Tidak ada saldo awal bank.</TableCell></TableRow> : preview.bank.map((b) => (
              <TableRow key={b.code} data-testid={`row-bank-${b.code}`}>
                <TableCell className="font-mono-num">{b.code}</TableCell>
                <TableCell>{b.name}</TableCell>
                <TableCell className="font-mono-num">{b.account_number || "-"}</TableCell>
                <TableCell className="font-mono-num">{b.coa_code}</TableCell>
                <TableCell className="text-right font-mono-num">{formatRupiah(b.amount)}</TableCell>
                {!isPosted && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit("BANK", b)} data-testid={`edit-bank-${b.code}`}>
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}</TableBody></Table>
        </CardContent>
      </Card>

      {/* Cash */}
      <Card>
        <CardContent className="p-4">
          <div className="font-display font-semibold mb-3">Saldo Awal Kas ({c.cash})</div>
          <Table><TableHeader><TableRow><TableHead>Kode</TableHead><TableHead>Nama</TableHead><TableHead>Akun COA</TableHead><TableHead className="text-right">Saldo Awal</TableHead>{!isPosted && <TableHead className="text-right">Aksi</TableHead>}</TableRow></TableHeader>
            <TableBody>{preview.cash.length === 0 ? <TableRow><TableCell colSpan={isPosted ? 4 : 5} className="text-center py-4 text-slate-500">Tidak ada saldo awal kas.</TableCell></TableRow> : preview.cash.map((b) => (
              <TableRow key={b.code} data-testid={`row-cash-${b.code}`}>
                <TableCell className="font-mono-num">{b.code}</TableCell>
                <TableCell>{b.name}</TableCell>
                <TableCell className="font-mono-num">{b.coa_code}</TableCell>
                <TableCell className="text-right font-mono-num">{formatRupiah(b.amount)}</TableCell>
                {!isPosted && (
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => openEdit("KAS", b)} data-testid={`edit-cash-${b.code}`}>
                      <Pencil className="h-4 w-4 mr-1" /> Edit
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}</TableBody></Table>
        </CardContent>
      </Card>

      {/* AR */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Saldo Awal Piutang (Otomatis: {c.ar_auto} + Manual: {c.ar_manual})</div>
            {!isPosted && (
              <Button size="sm" variant="outline" onClick={() => { setManualForm({ ...manualForm, kind: "AR", date: manualForm.date || migDate, due_date: manualForm.due_date || migDate }); setShowManual(true); }} data-testid="add-manual-ar-btn"><PlusCircle className="h-4 w-4 mr-1.5" />Tambah AR Manual</Button>
            )}
          </div>
          <Table><TableHeader><TableRow><TableHead>Sumber</TableHead><TableHead>Referensi</TableHead><TableHead>Pelanggan</TableHead><TableHead>Tanggal</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead>Mata Uang</TableHead><TableHead className="text-right">Kurs</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Rupiah</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {preview.ar_auto.length === 0 && preview.ar_manual.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-4 text-slate-500">Tidak ada saldo awal piutang.</TableCell></TableRow>}
              {preview.ar_auto.map((r) => (
                <TableRow key={r.id}><TableCell><Badge variant="outline">Auto</Badge></TableCell><TableCell className="font-mono-num">{r.reference}</TableCell><TableCell>{r.party_name}</TableCell><TableCell>{formatDateID(r.date)}</TableCell><TableCell>{formatDateID(r.due_date)}</TableCell><TableCell>{r.currency}</TableCell><TableCell className="text-right font-mono-num">{r.exchange_rate}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.outstanding_amount)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.amount_base)}</TableCell><TableCell></TableCell></TableRow>
              ))}
              {preview.ar_manual.map((r) => (
                <TableRow key={r.id} data-testid={`row-ar-manual-${r.id}`}><TableCell><Badge className="bg-amber-500">Manual</Badge></TableCell><TableCell className="font-mono-num">{r.reference || "-"}</TableCell><TableCell>{r.party_name || "-"}</TableCell><TableCell>{formatDateID(r.date)}</TableCell><TableCell>{formatDateID(r.due_date)}</TableCell><TableCell>{r.currency}</TableCell><TableCell className="text-right font-mono-num">{r.exchange_rate}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.outstanding_amount)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah((r.outstanding_amount || 0) * (r.exchange_rate || 1))}</TableCell><TableCell>{!isPosted && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`actions-ar-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => openEdit("AR", { ...r, kind: "AR" })} data-testid={`edit-ar-${r.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => deleteManual(r.id)} className="text-rose-600" data-testid={`delete-ar-${r.id}`}><Trash2 className="h-4 w-4 mr-2" /> Hapus</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}</TableCell></TableRow>
              ))}
            </TableBody></Table>
        </CardContent>
      </Card>

      {/* AP */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Saldo Awal Hutang (Otomatis: {c.ap_auto} + Manual: {c.ap_manual})</div>
            {!isPosted && (
              <Button size="sm" variant="outline" onClick={() => { setManualForm({ ...manualForm, kind: "AP", date: manualForm.date || migDate, due_date: manualForm.due_date || migDate }); setShowManual(true); }} data-testid="add-manual-ap-btn"><PlusCircle className="h-4 w-4 mr-1.5" />Tambah AP Manual</Button>
            )}
          </div>
          <Table><TableHeader><TableRow><TableHead>Sumber</TableHead><TableHead>Referensi</TableHead><TableHead>Pemasok</TableHead><TableHead>Tanggal</TableHead><TableHead>Jatuh Tempo</TableHead><TableHead>Mata Uang</TableHead><TableHead className="text-right">Kurs</TableHead><TableHead className="text-right">Outstanding</TableHead><TableHead className="text-right">Rupiah</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {preview.ap_auto.length === 0 && preview.ap_manual.length === 0 && <TableRow><TableCell colSpan={10} className="text-center py-4 text-slate-500">Tidak ada saldo awal hutang.</TableCell></TableRow>}
              {preview.ap_auto.map((r) => (
                <TableRow key={r.id}><TableCell><Badge variant="outline">Auto</Badge></TableCell><TableCell className="font-mono-num">{r.reference}</TableCell><TableCell>{r.party_name}</TableCell><TableCell>{formatDateID(r.date)}</TableCell><TableCell>{formatDateID(r.due_date)}</TableCell><TableCell>{r.currency}</TableCell><TableCell className="text-right font-mono-num">{r.exchange_rate}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.outstanding_amount)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.amount_base)}</TableCell><TableCell></TableCell></TableRow>
              ))}
              {preview.ap_manual.map((r) => (
                <TableRow key={r.id} data-testid={`row-ap-manual-${r.id}`}><TableCell><Badge className="bg-amber-500">Manual</Badge></TableCell><TableCell className="font-mono-num">{r.reference || "-"}</TableCell><TableCell>{r.party_name || "-"}</TableCell><TableCell>{formatDateID(r.date)}</TableCell><TableCell>{formatDateID(r.due_date)}</TableCell><TableCell>{r.currency}</TableCell><TableCell className="text-right font-mono-num">{r.exchange_rate}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.outstanding_amount)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah((r.outstanding_amount || 0) * (r.exchange_rate || 1))}</TableCell><TableCell>{!isPosted && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`actions-ap-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => openEdit("AP", { ...r, kind: "AP" })} data-testid={`edit-ap-${r.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => deleteManual(r.id)} className="text-rose-600" data-testid={`delete-ap-${r.id}`}><Trash2 className="h-4 w-4 mr-2" /> Hapus</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}</TableCell></TableRow>
              ))}
            </TableBody></Table>
        </CardContent>
      </Card>

      {/* Other manual */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="font-display font-semibold">Saldo Awal Akun Lainnya ({c.other_manual})</div>
            {!isPosted && (
              <Button size="sm" variant="outline" onClick={() => { setManualForm({ ...manualForm, kind: "OTHER", date: manualForm.date || migDate }); setShowManual(true); }} data-testid="add-manual-other-btn"><PlusCircle className="h-4 w-4 mr-1.5" />Tambah Entri Lain</Button>
            )}
          </div>
          <Table><TableHeader><TableRow><TableHead>Akun</TableHead><TableHead>Sisi</TableHead><TableHead>Deskripsi</TableHead><TableHead>Mata Uang</TableHead><TableHead className="text-right">Kurs</TableHead><TableHead className="text-right">Nilai</TableHead><TableHead className="text-right">Rupiah</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {preview.other_manual.length === 0 && <TableRow><TableCell colSpan={8} className="text-center py-4 text-slate-500">Tidak ada entri lain-lain.</TableCell></TableRow>}
              {preview.other_manual.map((r) => (
                <TableRow key={r.id} data-testid={`row-other-${r.id}`}><TableCell className="font-mono-num">{r.account_code}</TableCell><TableCell><Badge variant="outline">{r.debit_or_credit}</Badge></TableCell><TableCell>{r.notes || r.reference || "-"}</TableCell><TableCell>{r.currency}</TableCell><TableCell className="text-right font-mono-num">{r.exchange_rate}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah(r.outstanding_amount)}</TableCell><TableCell className="text-right font-mono-num">{formatRupiah((r.outstanding_amount || 0) * (r.exchange_rate || 1))}</TableCell><TableCell>{!isPosted && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="icon" variant="ghost" data-testid={`actions-other-${r.id}`}><MoreHorizontal className="h-4 w-4" /></Button></DropdownMenuTrigger>
                    <DropdownMenuContent align="end" className="bg-white">
                      <DropdownMenuItem onClick={() => openEdit("OTHER", { ...r, kind: "OTHER" })} data-testid={`edit-other-${r.id}`}><Pencil className="h-4 w-4 mr-2" /> Edit</DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={() => deleteManual(r.id)} className="text-rose-600" data-testid={`delete-other-${r.id}`}><Trash2 className="h-4 w-4 mr-2" /> Hapus</DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}</TableCell></TableRow>
              ))}
            </TableBody></Table>
        </CardContent>
      </Card>

      {/* Manual form — Dialog modal */}
      <Dialog open={showManual} onOpenChange={(open) => { if (!submittingManual) setShowManual(open); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="manual-dialog">
          <DialogHeader>
            <DialogTitle data-testid="manual-dialog-title">
              Tambah Entri Manual — {manualForm.kind === "AR" ? "Piutang (AR)" : manualForm.kind === "AP" ? "Hutang (AP)" : "Akun Lainnya"}
            </DialogTitle>
            <DialogDescription>
              Masukkan data saldo awal {manualForm.kind === "AR" ? "piutang pelanggan" : manualForm.kind === "AP" ? "hutang pemasok" : "akun lainnya"} secara manual. Entri akan ditambahkan ke daftar pratinjau dan harus di-posting bersama migrasi.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 py-2">
            {manualForm.kind !== "OTHER" && (
              <>
                <div><Label>Kode Rekanan</Label><Input value={manualForm.party_code} onChange={(e) => setManualForm({ ...manualForm, party_code: e.target.value })} data-testid="manual-party-code" /></div>
                <div><Label>Nama Rekanan</Label><Input value={manualForm.party_name} onChange={(e) => setManualForm({ ...manualForm, party_name: e.target.value })} data-testid="manual-party-name" /></div>
                <div><Label>No. Invoice/Referensi *</Label><Input value={manualForm.reference} onChange={(e) => setManualForm({ ...manualForm, reference: e.target.value })} data-testid="manual-reference" /></div>
              </>
            )}
            {manualForm.kind === "OTHER" && (
              <>
                <div><Label>Akun COA *</Label>
                  <Select value={manualForm.account_code} onValueChange={(v) => setManualForm({ ...manualForm, account_code: v })}>
                    <SelectTrigger data-testid="manual-account-select"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                    <SelectContent>{coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Sisi *</Label>
                  <Select value={manualForm.debit_or_credit} onValueChange={(v) => setManualForm({ ...manualForm, debit_or_credit: v })}>
                    <SelectTrigger data-testid="manual-side-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="DEBIT">Debit</SelectItem><SelectItem value="KREDIT">Kredit</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Keterangan</Label><Input value={manualForm.notes} onChange={(e) => setManualForm({ ...manualForm, notes: e.target.value })} data-testid="manual-notes" /></div>
              </>
            )}
            <div><Label>Tanggal *</Label><Input type="date" value={manualForm.date} onChange={(e) => setManualForm({ ...manualForm, date: e.target.value })} data-testid="manual-date" /></div>
            {manualForm.kind !== "OTHER" && <div><Label>Jatuh Tempo *</Label><Input type="date" value={manualForm.due_date} onChange={(e) => setManualForm({ ...manualForm, due_date: e.target.value })} data-testid="manual-due-date" /></div>}
            <div><Label>Mata Uang *</Label><Input value={manualForm.currency} onChange={(e) => setManualForm({ ...manualForm, currency: e.target.value.toUpperCase() })} data-testid="manual-currency" /></div>
            <div><Label>Kurs *</Label><Input type="number" step="0.0001" min="0" value={manualForm.exchange_rate} onChange={(e) => setManualForm({ ...manualForm, exchange_rate: parseFloat(e.target.value) || 1 })} data-testid="manual-rate" /></div>
            {manualForm.kind !== "OTHER" && <div><Label>Nilai Original</Label><Input type="number" min="0" value={manualForm.original_amount} onChange={(e) => setManualForm({ ...manualForm, original_amount: parseFloat(e.target.value) || 0 })} data-testid="manual-original" /></div>}
            <div><Label>Nilai Outstanding *</Label><Input type="number" min="0" value={manualForm.outstanding_amount} onChange={(e) => setManualForm({ ...manualForm, outstanding_amount: parseFloat(e.target.value) || 0 })} data-testid="manual-outstanding" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowManual(false)} disabled={submittingManual} data-testid="cancel-manual-btn">Batal</Button>
            <Button onClick={addManual} disabled={submittingManual} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-manual-btn">
              {submittingManual ? "Menyimpan..." : "Simpan Entri"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* EDIT DIALOG */}
      {editMode && editForm && (
        <Dialog open={!!editMode} onOpenChange={(v) => { if (!savingEdit && !v) closeEdit(); }}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-white" data-testid="edit-dialog">
            <DialogHeader>
              <DialogTitle data-testid="edit-dialog-title">
                Edit Saldo Awal — {editMode === "BANK" ? "Bank" : editMode === "KAS" ? "Kas" : editMode === "AR" ? "Piutang Manual" : editMode === "AP" ? "Hutang Manual" : "Akun Lainnya"}
              </DialogTitle>
              <DialogDescription>
                Perubahan hanya berlaku pada entri migrasi — jurnal yang sudah diposting tidak akan diubah.
                {(editMode === "BANK" || editMode === "KAS") && " Identitas rekening diambil dari master dan tidak dapat diubah di sini."}
              </DialogDescription>
            </DialogHeader>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 py-2">
              {editMode === "BANK" && (<>
                <div><Label>Kode Bank</Label><Input value={editForm.code} readOnly disabled className="font-mono-num" /></div>
                <div><Label>Nama Bank</Label><Input value={editForm.name || ""} readOnly disabled /></div>
                <div><Label>No. Rekening</Label><Input value={editForm.account_number || ""} readOnly disabled className="font-mono-num" /></div>
                <div><Label>Akun COA *</Label>
                  <Select value={editForm.coa_code || ""} onValueChange={(v) => setEditForm({ ...editForm, coa_code: v })}>
                    <SelectTrigger data-testid="edit-coa-select"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                    <SelectContent className="max-h-72">{coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="sm:col-span-2 lg:col-span-1"><Label>Saldo Awal *</Label>
                  <Input type="number" min="0" step="0.01" value={editForm.opening_balance} onChange={(e) => setEditForm({ ...editForm, opening_balance: parseFloat(e.target.value) || 0 })} className="font-mono-num" data-testid="edit-balance" />
                </div>
              </>)}

              {editMode === "KAS" && (<>
                <div><Label>Kode Kas</Label><Input value={editForm.code} readOnly disabled className="font-mono-num" /></div>
                <div><Label>Nama Kas</Label><Input value={editForm.name || ""} readOnly disabled /></div>
                <div><Label>Akun COA *</Label>
                  <Select value={editForm.coa_code || ""} onValueChange={(v) => setEditForm({ ...editForm, coa_code: v })}>
                    <SelectTrigger data-testid="edit-coa-select"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                    <SelectContent className="max-h-72">{coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Saldo Awal *</Label>
                  <Input type="number" min="0" step="0.01" value={editForm.opening_balance} onChange={(e) => setEditForm({ ...editForm, opening_balance: parseFloat(e.target.value) || 0 })} className="font-mono-num" data-testid="edit-balance" />
                </div>
              </>)}

              {(editMode === "AR" || editMode === "AP") && (<>
                <div><Label>Kode {editMode === "AR" ? "Pelanggan" : "Pemasok"}</Label><Input value={editForm.party_code || ""} onChange={(e) => setEditForm({ ...editForm, party_code: e.target.value })} data-testid="edit-party-code" /></div>
                <div><Label>Nama {editMode === "AR" ? "Pelanggan" : "Pemasok"}</Label><Input value={editForm.party_name || ""} onChange={(e) => setEditForm({ ...editForm, party_name: e.target.value })} data-testid="edit-party-name" /></div>
                <div><Label>Referensi *</Label><Input value={editForm.reference || ""} onChange={(e) => setEditForm({ ...editForm, reference: e.target.value })} data-testid="edit-reference" /></div>
                <div><Label>Tanggal *</Label><Input type="date" value={editForm.date || ""} onChange={(e) => setEditForm({ ...editForm, date: e.target.value })} data-testid="edit-date" /></div>
                <div><Label>Jatuh Tempo *</Label><Input type="date" value={editForm.due_date || ""} onChange={(e) => setEditForm({ ...editForm, due_date: e.target.value })} data-testid="edit-due-date" /></div>
                <div><Label>Mata Uang *</Label><Input value={editForm.currency || "IDR"} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value.toUpperCase() })} data-testid="edit-currency" /></div>
                <div><Label>Kurs *</Label><Input type="number" step="0.0001" min="0" value={editForm.exchange_rate} onChange={(e) => setEditForm({ ...editForm, exchange_rate: parseFloat(e.target.value) || 1 })} data-testid="edit-rate" /></div>
                <div><Label>Nilai Original</Label><Input type="number" min="0" value={editForm.original_amount} onChange={(e) => setEditForm({ ...editForm, original_amount: parseFloat(e.target.value) || 0 })} data-testid="edit-original" /></div>
                <div><Label>Nilai Outstanding *</Label><Input type="number" min="0" value={editForm.outstanding_amount} onChange={(e) => setEditForm({ ...editForm, outstanding_amount: parseFloat(e.target.value) || 0 })} className="font-mono-num" data-testid="edit-outstanding" /></div>
                <div className="sm:col-span-2 lg:col-span-3"><Label>Keterangan</Label><Input value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
              </>)}

              {editMode === "OTHER" && (<>
                <div><Label>Akun COA *</Label>
                  <Select value={editForm.account_code || ""} onValueChange={(v) => setEditForm({ ...editForm, account_code: v })}>
                    <SelectTrigger data-testid="edit-coa-select"><SelectValue placeholder="Pilih akun" /></SelectTrigger>
                    <SelectContent className="max-h-72">{coa.map((c) => <SelectItem key={c.code} value={c.code}>{c.code} — {c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div><Label>Sisi *</Label>
                  <Select value={editForm.debit_or_credit || "DEBIT"} onValueChange={(v) => setEditForm({ ...editForm, debit_or_credit: v })}>
                    <SelectTrigger data-testid="edit-side"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="DEBIT">Debit</SelectItem><SelectItem value="KREDIT">Kredit</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Mata Uang *</Label><Input value={editForm.currency || "IDR"} onChange={(e) => setEditForm({ ...editForm, currency: e.target.value.toUpperCase() })} /></div>
                <div><Label>Kurs *</Label><Input type="number" step="0.0001" min="0" value={editForm.exchange_rate} onChange={(e) => setEditForm({ ...editForm, exchange_rate: parseFloat(e.target.value) || 1 })} /></div>
                <div><Label>Nilai *</Label><Input type="number" min="0" value={editForm.outstanding_amount} onChange={(e) => setEditForm({ ...editForm, outstanding_amount: parseFloat(e.target.value) || 0 })} className="font-mono-num" data-testid="edit-outstanding" /></div>
                <div className="sm:col-span-2 lg:col-span-3"><Label>Deskripsi</Label><Input value={editForm.notes || ""} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} /></div>
              </>)}

              <div className="sm:col-span-2 lg:col-span-3"><Label>Alasan Perubahan (opsional)</Label>
                <Textarea rows={2} value={editForm.edit_reason || ""} onChange={(e) => setEditForm({ ...editForm, edit_reason: e.target.value })} placeholder="Contoh: koreksi angka hasil rekonsiliasi" data-testid="edit-reason" />
              </div>
            </div>

            {confirmEdit && (
              <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
                <div className="font-semibold">Simpan perubahan data migrasi?</div>
                <div>Perubahan ini akan memperbarui perhitungan saldo awal dan pratinjau jurnal migrasi.</div>
              </div>
            )}

            <DialogFooter className="gap-2">
              <Button variant="outline" onClick={closeEdit} disabled={savingEdit} data-testid="cancel-edit-btn">Batal</Button>
              <Button onClick={saveEdit} disabled={savingEdit} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="save-edit-btn">
                {savingEdit ? "Menyimpan..." : (confirmEdit ? "Konfirmasi Simpan" : "Simpan Perubahan")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Balance warning */}
      {!isPosted && Math.abs(t.difference) >= 0.01 && (
        <Card className="border-amber-400 border-2 bg-amber-50">
          <CardContent className="p-4 flex items-start gap-3">
            <ShieldAlert className="h-5 w-5 mt-0.5 text-amber-700" />
            <div className="text-sm text-amber-900" data-testid="balance-warning">
              <div className="font-semibold">Migrasi belum seimbang.</div>
              <div>Periksa kembali data sebelum posting. Total Debit {formatRupiah(t.posting_debit)} ≠ Total Kredit {formatRupiah(t.posting_credit)} (selisih {formatRupiah(t.difference)}).</div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Post action */}
      <Card className={canPost ? "border-emerald-400 border-2" : "border-slate-200"}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-start gap-3">
              <ShieldAlert className="h-5 w-5 mt-1 text-amber-600" />
              <div className="text-sm">
                <div className="font-semibold">Konfirmasi Posting</div>
                <div className="text-slate-600">Migrasi akan menghasilkan satu jurnal <span className="font-mono-num">OPENING_BALANCE</span> yang tidak dapat diubah setelah diposting.</div>
              </div>
            </div>
            <Button onClick={postOpening} disabled={!canPost || loading} className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="post-opening-btn">
              <PlayCircle className="h-5 w-5 mr-2" /> Posting Saldo Awal
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default OpeningBalancePage;
